/* eslint-disable react/no-unescaped-entities */
import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Lock } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-900 mb-10 transition-colors" data-testid="back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="max-w-sm w-full">
          <div className="w-10 h-10 rounded-xl bg-charcoal-900 grid place-items-center mb-6">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900">Choose a new password</h1>
          <p className="text-charcoal-500 mt-2 text-sm">Pick something you'll remember. At least 6 characters.</p>

          {!token ? (
            <div className="mt-8 rounded-2xl bg-red-50 border border-red-100 p-5" data-testid="reset-invalid">
              <p className="text-sm font-medium text-red-900">This reset link is missing a token.</p>
              <p className="text-sm text-red-800 mt-1">Please request a new link from the forgot-password page.</p>
              <Link to="/forgot-password" className="inline-block mt-3 text-sm font-medium text-red-900 hover:underline">
                Request a new link →
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-xs font-medium text-charcoal-700">New password</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" />
                  <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                    placeholder="At least 6 characters" data-testid="reset-password" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal-700">Confirm password</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" />
                  <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="w-full h-11 pl-10 pr-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                    placeholder="Type it again" data-testid="reset-confirm" />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full h-11 rounded-xl bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
                data-testid="reset-submit">
                {loading ? "Saving…" : "Save new password"}
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative bg-charcoal-900 tech-grid overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-center px-16">
          <p className="text-emerald-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">Almost there</p>
          <p className="text-white text-3xl font-medium leading-snug tracking-tight max-w-md">
            "Your mission is safe with us. Choose a strong new password and you're back in."
          </p>
        </div>
      </div>
    </div>
  );
}
